import { Inject, Options, Vue } from "vue-property-decorator";
import { TrustAPI } from "@/shared/modules/trust-api";
import store from "@/trust-admin-app/store";
import FormatDateTimeMixin from '@/shared/mixins/format-datetime';
import { RouteN } from "@/trust-admin-app/constants/router-name";
import { InvestigationView, NoteView, PracticeAnalysisRequest, PracticeAnalysisView, PracticeView } from "@/shared/modules/typescript-trustapi-client/models";
import { AppInsights } from "@/trust-admin-app/appInsights";
import { EventEmitter } from "events";
import { orderBy } from "lodash";

@Options({
  components: {},
  mixins: [FormatDateTimeMixin]
})
export default class PracticeDashboard extends Vue {
  private ai = new AppInsights;

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  private associatedEntitiesContent = '';
  private lawPracticeNotes = '';

  private practiceAnalyses: PracticeAnalysisView[] = [];
  private editPracticeAnalyses = false;
  private canSavePracticeAnalyses = false;

  private investigations: InvestigationView[] = [];
  private notes: NoteView[] = [];

  created(): void {
    this.associatedEntitiesContent = this.selectedPractice.associatedEntities;
    this.lawPracticeNotes = this.selectedPractice.lawPracticeNotes;
    this.getNotes();
    this.getInvestigation();
  }

  mounted(): void {
    window.scrollTo(0, 0);
    this.practiceAnalyses = JSON.parse(JSON.stringify(this.selectedPractice.practiceAnalyses));
    this.eventCtrl.on('reload-investigation-data', this.getInvestigation);
  }

  beforeUnmount(): void {
    this.eventCtrl.removeListener('reload-investigation-data', this.getInvestigation);
  }

  private async saveLawPracticeNotes(): Promise<void> {
    try {
      await this.apiService.PracticeApi?.apiV2PracticeEditLawPracticeNotesPost(
        {
          id: this.selectedPractice.id,
          lawPracticeNotes: this.lawPracticeNotes
        }
      );

      const resp1: any = await this.apiService.PracticeApi?.apiV2PracticeGetPracticeByIdPracticeIdGet(this.selectedPractice.id);
      this.eventCtrl.emit('show-snackbar', 'success', 'Law practice notes saved successfully.');
      store.dispatch('setSelectedPractice', resp1.data.data as PracticeView);
    } catch(error) {
      this.ai.trackException('Unable to save Law practice note: ' + error);
      this.eventCtrl.emit('show-snackbar', 'error', 'Unable to save Law practice notes ' + error);
    }

  }

  private async saveAssociatedEntities(): Promise<void> {

    try {
      await this.apiService.PracticeApi?.apiV2PracticeEditAssociatedEntitiesPost(
        {
          id: this.selectedPractice.id,
          associatedEntities: this.associatedEntitiesContent
        }
      );

      const resp1: any = await this.apiService.PracticeApi?.apiV2PracticeGetPracticeByIdPracticeIdGet(this.selectedPractice.id);
      this.eventCtrl.emit('show-snackbar', 'success', 'Associated entities saved successfully.');
      store.dispatch('setSelectedPractice', resp1.data.data as PracticeView);
    } catch(error) {
      this.ai.trackException('Unable to save Associated entities: ' + error);
      this.eventCtrl.emit('show-snackbar', 'error', 'Unable to save Associated entities ' + error);
    }
  }

  private async getNotes(): Promise<void> {
    try {
      // this.loadingContent = true;
      // this.errorLoadingContent = false;

      const resp: any = await this.apiService.NotesApi?.apiV2NotesGetNotesByPracticeIdPost(
        {
          practiceId: this.selectedPractice.id,
          showRiskOnly: false
        }
      );

      this.notes = resp.data.data;
      this.notes = orderBy(
        this.notes,
        ['id', 'createdOn'],
        ['asc', 'desc']
      );

    } catch (err) {
      // this.errorLoadingContent = true;
      console.log(err);
    } finally {
      // this.loadingContent = false;
      // console.log('finally');
    }
  }

  private async getInvestigation(): Promise<void> {
    try {
      const resp: any = await this.apiService.InvestigationApi?.apiV2InvestigationGetInvestigationsByPracticeIdPracticeIdGet(
        this.selectedPractice.id
      );
      this.investigations = resp.data.data;
    } catch (err: any) {
      this.ai.trackException('Unable to get Investigation: ' + err);
      this.eventCtrl.emit('show-snackbar', 'error', 'Unable to get Investigation: ' + err.message);
      console.log(err);
    }
  }

  private goToAllInvestigations(): void {
    this.$router.push({
      name: RouteN.TA.PracticeInvestigationDetails.n,
      query: {
        pId: this.selectedPractice.id
      }
    });
  }


  get selectedPractice(): PracticeView {
    return store.getters.getSelectedPractice;
  }

  private cancelEditPracticeAnalyses() {
    this.practiceAnalyses = JSON.parse(JSON.stringify(this.selectedPractice.practiceAnalyses));
    this.editPracticeAnalyses = false;
  }

  private saveCheckPracticeAnalyses() {
    this.canSavePracticeAnalyses = this.practiceAnalyses.filter(p => p.answer !== null).length > 0;
  }

  private savePracticeAnalyses() {
    const _request: PracticeAnalysisRequest[] = this.practiceAnalyses.filter(p => p.answer !== null);
    if (_request.length > 0) {
      this.apiService.PracticeApi?.apiV2PracticeUpdatePracticeAnalysesPost(_request)
        .then(() => {
          this.editPracticeAnalyses = false;
          this.canSavePracticeAnalyses = false;
          this.selectedPractice.practiceAnalyses = this.practiceAnalyses;
          store.dispatch('setSelectedPractice', this.selectedPractice);
          this.eventCtrl.emit('show-snackbar', 'success', 'Practice analyses updated');
        })
        .catch(err => {
          this.ai.trackException('Unable to save practice analyses: ' + err);
          this.eventCtrl.emit('show-snackbar', 'error', 'Unable to save practice analyses ' + err);
        })
    }
  }

  private viewLastInvestigation() {
    const _lastInvestigation: InvestigationView = orderBy( this.investigations, 'commencementDate', 'desc')[0];
    if(_lastInvestigation) {
      this.showInvestigationDetails(_lastInvestigation);
    }
  }

  private showInvestigationDetails(i: InvestigationView) {
    this.eventCtrl.emit('showInvestigationDetailsModal', i);
  }

  private async showInvestigationDetailsByNote(n: InvestigationView) {
    await this.apiService.InvestigationApi?.apiV2InvestigationGetInvestigationsByPracticeIdPracticeIdGet(
      this.selectedPractice.id
    )
      .then(res => {
        this.investigations = res.data.data;
        const _inv: InvestigationView[] = this.investigations.filter(i => {
          return i.id = n.id;
        });
        if (_inv.length > 0)
          this.eventCtrl.emit('showInvestigationDetailsModal', _inv[0]);
      })
      .catch(err => {
        console.log(err);
        this.ai.trackException('Unable to get Investigation details: ' + err);
        this.eventCtrl.emit('show-snackbar', 'error', 'Unable to get Investigation details: ' + err.message);
      })
  }

  private viewAllInvestigatorNotes() {
    this.$router.push({
      name: RouteN.TA.PracticeNotes.n,
      query: {
        pId: this.selectedPractice.id,
      }
    });
  }

}

